/**
 * Route Registry , single source of truth for which chrome a route renders.
 *
 * Every protected route is classified so the dashboard layout, POS layout, and
 * sidebar can render the correct chrome without fragile string matching.
 *
 * @location lib/routing/route-registry.ts
 */

import type { NavSurface } from '@/lib/business-types/registry';

// ─── Classification ──────────────────────────────────────────────────────────

/**
 * Determines which chrome shell a route renders.
 *
 * - `full`:      Sidebar + breadcrumbs + mobile nav (standard dashboard pages)
 * - `focused`:   Full-screen editor, no sidebar/breadcrumbs/mobile nav
 * - `minimal`:   Standalone page, no shared chrome (onboarding, setup wizards)
 * - `pos`:       POS shell (TopBar + BottomNav, no dashboard chrome)
 */
export type RouteClassification = 'full' | 'focused' | 'minimal' | 'pos';

// ─── Route Entry ─────────────────────────────────────────────────────────────

type RouteEntry = {
  /** URL pattern , exact match or prefix match (trailing /). */
  pattern: string;
  /** Whether this is an exact match or prefix match. */
  exact?: boolean;
  /** Which chrome to render. */
  classification: RouteClassification;
  /** Human-readable name for debugging/logging. */
  name: string;
  /** Surface key for permission gating (used by resolveSurface). */
  surface?: NavSurface;
  /** Whether this route requires an active branch context. */
  requiresBranch?: boolean;
};

// ─── The Registry ────────────────────────────────────────────────────────────
// Ordered longest-prefix-first so more specific patterns match before broader ones.
// POS routes are listed separately since they use a different prefix structure.

const DASHBOARD_ROUTES: RouteEntry[] = [
  // ── Focused workspaces (no sidebar) ──
  { pattern: '/dashboard/templates/preview', classification: 'focused', name: 'Template Preview', surface: 'templates', requiresBranch: true },
  { pattern: '/dashboard/menus/manage/', classification: 'focused', name: 'Menu Editor', surface: 'menus', requiresBranch: true },

  // ── Minimal chrome (standalone pages) ──
  { pattern: '/dashboard/onboarding', classification: 'minimal', name: 'Onboarding' },

  // ── Full chrome routes (sidebar + breadcrumbs + mobile nav) ──
  // These are the default , they don't need explicit entries unless they
  // carry surface metadata. Listed here for documentation completeness.
  { pattern: '/dashboard/templates/p', classification: 'focused', name: 'Public Template Preview' },
];

/**
 * Routes that bypass the dashboard layout entirely (POS shell renders its own chrome).
 * Checked by both dashboard/layout.tsx and POS layout.
 */
const POS_PREFIX = '/pos/';

// ─── Matching Logic ──────────────────────────────────────────────────────────

/**
 * Find the best matching route entry for a given pathname.
 * Dashboard routes: longest prefix match wins.
 * POS routes: any path starting with /pos/ is classified as 'pos'.
 */
function findBestMatch(pathname: string): RouteEntry | null {
  // POS routes are handled by their own layout , never match dashboard routes
  if (pathname.startsWith(POS_PREFIX)) return null;

  // Check dashboard routes (longest prefix first due to sort order)
  for (const entry of DASHBOARD_ROUTES) {
    if (entry.exact) {
      if (pathname === entry.pattern) return entry;
    } else {
      if (pathname === entry.pattern || pathname.startsWith(`${entry.pattern}/`)) {
        return entry;
      }
    }
  }

  return null;
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Classify a pathname to determine which chrome it should render.
 *
 * @example
 * classifyRoute('/dashboard')           → 'full'
 * classifyRoute('/dashboard/templates/preview') → 'focused'
 * classifyRoute('/dashboard/onboarding') → 'minimal'
 * classifyRoute('/pos/abc123/floor')    → 'pos' (handled by POS layout, not this registry)
 */
export function classifyRoute(pathname: string | null | undefined): RouteClassification {
  if (!pathname) return 'full';

  // POS routes use their own layout , this is a fallback check
  if (pathname.startsWith(POS_PREFIX)) return 'pos';

  const match = findBestMatch(pathname);
  return match?.classification ?? 'full';
}

/**
 * Get the full metadata for a matched route.
 * Returns null if the path doesn't match any registered route (defaults to full chrome).
 */
export function getRouteMeta(pathname: string | null | undefined): RouteEntry | null {
  if (!pathname) return null;
  if (pathname.startsWith(POS_PREFIX)) return null;
  return findBestMatch(pathname);
}

/**
 * Check if a route should hide the dashboard sidebar.
 * Useful for sidebar components that need to know if they should render.
 */
export function shouldHideSidebar(pathname: string | null | undefined): boolean {
  const classification = classifyRoute(pathname);
  return classification === 'focused' || classification === 'minimal';
}

/**
 * Check if a route should hide the mobile bottom nav.
 */
export function shouldHideMobileNav(pathname: string | null | undefined): boolean {
  const classification = classifyRoute(pathname);
  return classification !== 'full';
}

/**
 * Check if a route is a POS route (uses POS shell, not dashboard chrome).
 */
export function isPosRoute(pathname: string | null | undefined): boolean {
  return Boolean(pathname && pathname.startsWith(POS_PREFIX));
}

// ─── POS Route Helpers ───────────────────────────────────────────────────────
// POS routes exist in two forms:
//   Internal:  /pos/{branchId}/{workspace}
//   Platform:  /{branchId}/{workspace}  (via proxy)
// These helpers parse both formats consistently.

/**
 * Extract the branch ID from a POS pathname.
 * Works for both /pos/{branchId}/... and /{branchId}/... formats.
 */
export function extractPosBranchId(pathname: string | null | undefined): string | null {
  if (!pathname) return null;

  if (pathname.startsWith(POS_PREFIX)) {
    // /pos/{branchId}/... → extract segment after /pos/
    const afterPrefix = pathname.slice(POS_PREFIX.length);
    const segment = afterPrefix.split('/')[0];
    return segment || null;
  }

  return null;
}

/**
 * Extract the workspace segment from a POS pathname.
 * Returns 'floor', 'counter', 'kds', 'settings', 'setup', etc.
 */
export function extractPosWorkspace(pathname: string | null | undefined): string {
  if (!pathname) return '';

  const internalPrefix = '/pos/';
  if (pathname.startsWith(internalPrefix)) {
    const remainder = pathname.slice(internalPrefix.length);
    // Skip the branchId segment, take the workspace
    const segments = remainder.split('/').filter(Boolean);
    return segments[1] || segments[0] || '';
  }

  return '';
}

/**
 * Check if a POS pathname is a setup route (bypasses PosShell).
 */
export function isPosSetupRoute(pathname: string | null | undefined, branchId: string): boolean {
  if (!pathname || !branchId) return false;
  return (
    pathname.includes(`/${branchId}/setup`) ||
    pathname === `/pos/${branchId}/setup`
  );
}

/**
 * Is this the Operations router? Doc 69 §9.
 *
 * Operations is deliberately NOT a POS workspace and is not in `POS_WORKSPACES`.
 * It is a router INTO the workspaces: it shows a count per lane and deep-links to
 * the board that owns each one, so work still happens in exactly one place per
 * job. Registering it as a workspace would mean every business type had to
 * declare whether it "has" Operations, and every workstation had to allow it ,
 * for a screen whose entire content is derived from the other workspaces.
 *
 * So it bypasses the workspace allowlist redirect, the same way setup and the KDS
 * display do. Access is still enforced: the API authorises against the union of
 * the board permissions, and each lane is filtered by the caller's own grants, so
 * a cashier without dispatch permission sees no dispatch row.
 */
export function isPosOperationsRoute(pathname: string | null | undefined, branchId: string): boolean {
  if (!pathname || !branchId) return false;
  return pathname === `/pos/${branchId}/operations` || pathname.includes(`/${branchId}/operations`);
}

/**
 * Check if a POS pathname is the KDS display route (fullscreen, no chrome).
 */
export function isPosKdsDisplayRoute(pathname: string | null | undefined, branchId: string): boolean {
  if (!pathname || !branchId) return false;
  return (
    pathname === `/pos/${branchId}/kds/display` ||
    pathname === `/${branchId}/kds/display`
  );
}
